import { SharedState } from "../SharedProvider";
import { createSelector } from "@reduxjs/toolkit";
import { selectCurrentRobotStateStatusRoomId } from "../status/status-selectors";

export const selectSelectedRoomId = (state: SharedState) => state.rooms.rooms.selectedId;

export const selectFloorsData = (state: SharedState) => state.rooms.floors;
export const selectFloors = (state: SharedState) => state.rooms.floors.data;
export const selectFloorsRestSet = (state: SharedState) => state.rooms.floors.rest;

export const selectRoomsData = (state: SharedState) => state.rooms.rooms;
export const selectRooms = (state: SharedState) => state.rooms.rooms.data;
export const selectRoomsRestSet = (state: SharedState) => state.rooms.rooms.rest;
export const selectSelectedRoom = createSelector([selectSelectedRoomId, selectRooms], (roomId, rooms) => {
  return rooms.find((r) => r.id === roomId);
});
export const selectDownloadFilesLink = (state: SharedState) => state.rooms.downloadFilesLink;

export const selectRoomById = createSelector(
  [selectRoomsData, (_state: SharedState, roomId: string) => roomId],
  (rooms, roomId) => {
    return rooms.data.find((room) => room.id === roomId) ?? null;
  }
);

export const selectCurrentRobotCurrentRoom = createSelector(
  [selectCurrentRobotStateStatusRoomId, selectRooms],
  (roomId, rooms) => rooms.find((r) => r.id === roomId)
);

export const selectRoomButtonConfiguration = createSelector([selectSelectedRoom], (room) => room?.buttonConfiguration);
export const selectSelectedFloor = createSelector([selectSelectedRoom, selectFloors], (room, floors) =>
  floors.find((f) => f.id === room?.floorId)
);

export const selectCleaningZonesData = (state: SharedState) => state.rooms.cleaningZones;
export const selectCleaningZones = (state: SharedState) => state.rooms.cleaningZones.data;
export const selectAllCleaningZonesIdName = createSelector([selectCleaningZones], (cleaningZones) => {
  return cleaningZones.map((cz) => ({ id: cz.id, name: cz.name }));
});
export const selectSelectedRoomCleaningZones = createSelector(
  [selectCleaningZones, selectSelectedRoomId],
  (cleaningZones, roomId) => cleaningZones.filter((cz) => cz.room === roomId)
);
export const selectRoomCleaningZones = createSelector(
  // Memoization isn't working for dynamic id passed in without component refresh, so doing the filter in the component ensures correct dynamic data list
  [selectCleaningZones],
  (cleaningZones) => (roomId: string) => cleaningZones.filter((cz) => cz.room === roomId)
);

export const selectRepeatPathsData = (state: SharedState) =>state.rooms.teachRepeatPaths;
export const selectRepeatPaths = (state: SharedState) => state.rooms.teachRepeatPaths.data;

export const selectRedZonesData = (state: SharedState) => state.rooms.redZones;
export const selectRedZones = (state: SharedState) => state.rooms.redZones.data;
export const selectSelectedRoomRedZones = createSelector([selectRedZones, selectSelectedRoomId], (redZones, roomId) =>
  redZones.filter((rz) => rz.room === roomId)
);
export const selectRoomRedZones = createSelector(
  // Memoization isn't working for dynamic id passed in without component refresh, so doing the filter in the component ensures correct dynamic data list
  [selectRedZones],
  (redZones) => (roomId: string) => redZones.filter((rz) => rz.room === roomId)
);

export const selectCurrentRobotCurrentRoomRedZones = createSelector(
  [selectRedZones, selectCurrentRobotStateStatusRoomId],
  (redZones, roomId) => redZones.filter((rz) => rz.room === roomId)
);

export const selectRuleZonesData = (state: SharedState) => state.rooms.ruleZones;
export const selectRuleZones = (state: SharedState) => state.rooms.ruleZones.data;
export const selectSelectedRoomRuleZones = createSelector([selectRuleZones, selectSelectedRoomId], (ruleZones, roomId) =>
  ruleZones.filter((rz) => rz.room === roomId)
);
export const selectRoomRuleZones = createSelector(
  // Memoization isn't working for dynamic id passed in without component refresh, so doing the filter in the component ensures correct dynamic data list
  [selectRuleZones],
  (ruleZones) => (roomId: string) => ruleZones.filter((rz) => rz.room === roomId),
);

export const selectWaypointsData = (state: SharedState) => state.rooms.waypoints;
export const selectWaypoints = (state: SharedState) => state.rooms.waypoints.data;
export const selectSelectedRoomWaypoints = createSelector(
  [selectWaypoints, selectSelectedRoomId],
  (waypoints, roomId) => ({
    edges: waypoints.edges.filter((e) => e.room === roomId),
    nodes: waypoints.nodes.filter((n) => n.room === roomId),
  })
);
export const selectUnsavedWaypoints = (state: SharedState) => state.rooms.waypoints.unsavedWaypoints;

export const selectGatesData = (state: SharedState) => state.rooms.gates;
export const selectGates = (state: SharedState) => state.rooms.gates.data;
export const selectSelectedRoomGates = createSelector([selectGates, selectSelectedRoomId], (gates, roomId) =>
  gates.filter((g) => g.room === roomId),
);

export const selectLandmarksData = (state: SharedState) => state.rooms.landmarks;
export const selectLandmarks = (state: SharedState) => state.rooms.landmarks.data;
export const selectAllLandmarksIdName = createSelector([selectLandmarks], (landmarks) => {
  return landmarks.map((lm) => ({ id: lm.id, name: lm.name }));
});
export const selectSelectedRoomLandmarks = createSelector(
  [selectLandmarks, selectSelectedRoomId],
  (landmarks, roomId) => landmarks.filter((lm) => lm.room === roomId)
);

export const selectLimitZonesData = (state: SharedState) => state.rooms.limitZones;
export const selectLimitZones = (state: SharedState) => state.rooms.limitZones.data;
export const selectSelectedRoomLimitZone = createSelector(
  [selectLimitZones, selectSelectedRoomId],
  (limitZones, roomId) => limitZones.find((lz) => lz.room === roomId)
);

export const selectMissionsData = (state: SharedState) => state.rooms.missions;
export const selectGlobalMissions = (state: SharedState) => state.rooms.missions.globalMissions;
export const selectMissions = (state: SharedState) => state.rooms.missions.data;
export const selectSelectedRoomMissions = createSelector([selectMissions, selectSelectedRoomId], (missions, roomId) =>
  missions.filter((m) => m.room === roomId)
);
export const selectRoomMissions = createSelector([selectMissions, selectRoomById], (missions, room) =>
  missions.filter((m) => m.room === room?.id)
);
export const selectMissionsRestSet = (state: SharedState) => state.rooms.missions.rest;

export const selectCurrentRobotMissions = createSelector(
  [selectMissions, selectCurrentRobotStateStatusRoomId],
  (missions, roomId) => missions.filter((m) => m.room === roomId)
);

export const selectSelectedRoomZoneIdsAndNames = createSelector(
  [selectSelectedRoomCleaningZones, selectSelectedRoomRedZones, selectSelectedRoomRuleZones],
  (cleaningZones, redZones, ruleZones) => {
    return cleaningZones
      .map((cz) => ({ id: cz.id, name: cz.name }))
      .concat(redZones.map((rz) => ({ id: rz.id, name: rz.name })))
      .concat(ruleZones.map((rz) => ({ id: rz.id, name: rz.name })));
  }
);
export const selectSelectedRoomGateIdsIdentifiersAndNames = createSelector([selectSelectedRoomGates], (gates) =>
  gates.map((g) => ({ id: g.id, identifier: g.identifier, name: g.name })),
);